// IntakePortal — PUBLIC consumer destination for a Take-It-Home QR code.
// No auth: the opaque token in the URL is the only credential. Mobile-first,
// consumer-controlled, progressive. Verification, communication permission,
// product interest and submission are kept as SEPARATE documented events.
// The QR starting this never creates universal consent — every channel grant
// is the consumer's own explicit selection, and nothing is preselected.
//
// Phase-1 scope: 4 core steps (coverage interest → contact → communication
// preferences → review/submit) with save-and-resume. Product-specific steps
// (ACA income, Medicare SOA, life underwriting, etc.) are sequenced separately
// and must route through the agency's approved product workflows.

import React from 'react';

const T = {
  ground: '#F4F7FA', ink: '#0B2233', sub: '#4A5D6B', line: '#DCE6EC',
  brand: '#0E7C86', brandDark: '#0A5F67', accent: '#F0A020', ok: '#2E8B57', card: '#FFFFFF',
};
const font = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
const PRODUCTS = [['aca', 'ACA Marketplace'], ['medicare', 'Medicare'], ['life', 'Life Insurance'], ['ppo', 'Private Health / PPO'], ['dental', 'Dental'], ['vision', 'Vision']];
const CHANNELS = [
  ['call', 'Phone call', 'A licensed agent may call you.'],
  ['manual_sms', 'Text from your agent', 'Personal texts sent by your agent.'],
  ['automated_sms', 'Automated text messages', 'Reminders and updates sent automatically. Message & data rates may apply. Consent is not a condition of purchase.'],
  ['email', 'Email', 'Coverage details and follow-up by email.'],
  ['appointment_reminder', 'Appointment reminders', 'Reminders about scheduled appointments.'],
];

const api = async (payload, method = 'POST') => {
  const r = await fetch('/api/public/intake', method === 'GET' ? undefined : { method, headers: { 'content-type': 'application/json' }, body: JSON.stringify(payload) });
  return { ok: r.ok, status: r.status, data: await r.json().catch(() => ({})) };
};

export default function IntakePortal({ token }) {
  const [phase, setPhase] = React.useState('loading'); // loading | expired | landing | intake | done | error
  const [qr, setQr] = React.useState(null);
  const [resume, setResume] = React.useState(null);
  const [step, setStep] = React.useState(1);
  const [a, setA] = React.useState({ consent: { grants: [] } });
  const [reference, setReference] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const up = (patch) => setA((s) => ({ ...s, ...patch }));

  // Resolve QR + resume an in-progress session (?r= in the URL).
  React.useEffect(() => {
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const rt = params.get('r');
      const r = await fetch(`/api/public/intake?token=${encodeURIComponent(token)}`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setPhase('error'); return; }
      if (d.expired) { setQr(d.qr); setPhase('expired'); return; }
      setQr(d.qr);
      if (rt) {
        const s = await api({ op: 'resume', resume_token: rt });
        if (s.ok && s.data.session) { setResume(rt); setA(s.data.session.answers || { consent: { grants: [] } }); setStep(s.data.session.current_step || 1); if (s.data.session.reference_number) { setReference(s.data.session.reference_number); setPhase('done'); return; } setPhase('intake'); return; }
      }
      setPhase('landing');
    })();
  }, [token]);

  const start = async () => {
    setBusy(true);
    const r = await api({ op: 'start', token });
    setBusy(false);
    if (r.ok) { setResume(r.data.resume_token); const u = new URL(window.location.href); u.searchParams.set('r', r.data.resume_token); window.history.replaceState({}, '', u); setStep(1); setPhase('intake'); }
  };
  const save = async (nextStep, pct) => { if (resume) await api({ op: 'save', resume_token: resume, step: nextStep, answers: a, completion_pct: pct }); };
  const go = async (n, pct) => { await save(n, pct); setStep(n); window.scrollTo(0, 0); };
  const submit = async () => {
    setBusy(true);
    await save(4, 90);
    const r = await api({ op: 'submit', resume_token: resume });
    setBusy(false);
    if (r.ok) { setReference(r.data.reference_number); setPhase('done'); }
  };

  const wrap = (children) => <div style={{ minHeight: '100vh', background: T.ground, color: T.ink, fontFamily: font, padding: '0 0 48px' }}><div style={{ maxWidth: 560, margin: '0 auto', padding: '0 18px' }}>{children}</div></div>;

  if (phase === 'loading') return wrap(<div style={{ paddingTop: 120, textAlign: 'center', color: T.sub }}>Loading…</div>);
  if (phase === 'error') return wrap(<Center title="This link isn't valid" body="Please ask your agent for a new link." />);
  if (phase === 'expired') return wrap(<Center title="This link has expired" body="Please contact the agency for an updated link." />);

  const Header = () => (
    <div style={{ paddingTop: 22, display: 'flex', alignItems: 'center', gap: 12 }}>
      {qr?.agent_photo ? <img src={qr.agent_photo} alt="" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover' }} /> : <div style={{ width: 44, height: 44, borderRadius: '50%', background: T.brand, color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 700 }}>{(qr?.agency_name || 'S')[0]}</div>}
      <div style={{ lineHeight: 1.25 }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>{qr?.agency_name}</div>
        <div style={{ fontSize: 12.5, color: T.sub }}>Your agent: {qr?.agent_name}</div>
      </div>
      <span style={{ marginLeft: 'auto', fontSize: 11, color: T.ok, display: 'flex', alignItems: 'center', gap: 4 }} title="Secure connection">🔒 Secure</span>
    </div>
  );

  if (phase === 'done') return wrap(<><Header /><div style={{ background: T.card, border: `1px solid ${T.line}`, borderRadius: 16, padding: '28px 22px', marginTop: 26, textAlign: 'center' }}>
    <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(46,139,87,0.12)', color: T.ok, display: 'grid', placeItems: 'center', fontSize: 28, margin: '0 auto 14px' }}>✓</div>
    <h1 style={{ fontSize: 21, margin: '0 0 8px' }}>Your information was submitted</h1>
    <p style={{ color: T.sub, fontSize: 14.5, lineHeight: 1.55, margin: '0 0 16px' }}>{qr?.agent_name} will follow up using the contact preferences you selected. Thank you.</p>
    <div style={{ fontSize: 12.5, color: T.sub }}>Reference number</div>
    <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 16, fontWeight: 700, letterSpacing: '0.02em' }}>{reference}</div>
  </div><p style={{ textAlign: 'center', fontSize: 11.5, color: T.sub, marginTop: 16 }}>You'll receive communications only on the channels you approved.</p></>);

  if (phase === 'landing') return wrap(<><Header />
    <div style={{ marginTop: 30 }}>
      <h1 style={{ fontSize: 27, lineHeight: 1.18, margin: '0 0 12px', textWrap: 'balance' }}>Let's find the right next step</h1>
      <p style={{ color: T.sub, fontSize: 15, lineHeight: 1.6, margin: '0 0 20px' }}>Complete your information securely and an authorized agent will follow up based on the preferences you select. It takes about 5 minutes, and you can save and finish later.</p>
      {qr?.product_line && <Pill>Coverage interest: {PRODUCTS.find((p) => p[0] === qr.product_line)?.[1] || qr.product_line}</Pill>}
      <ul style={{ listStyle: 'none', padding: 0, margin: '20px 0 24px', display: 'grid', gap: 10 }}>
        {['You control what you share and how we contact you', 'Your information goes securely to the agency', 'You are not required to finish right now'].map((x) => <li key={x} style={{ display: 'flex', gap: 10, fontSize: 13.5, color: T.sub }}><span style={{ color: T.brand }}>✓</span>{x}</li>)}
      </ul>
      <button onClick={start} disabled={busy} style={btn(true)}>{busy ? '…' : 'Start secure intake'}</button>
      <p style={{ fontSize: 11, color: T.sub, textAlign: 'center', marginTop: 14 }}>By continuing you agree the agency may store your responses. Communication permissions are chosen separately, later in this form.</p>
    </div></>);

  // ── Intake steps ──────────────────────────────────────────────
  const TOTAL = 4;
  return wrap(<><Header />
    <Progress step={step} total={TOTAL} label={['Coverage interest', 'About you', 'How we may contact you', 'Review & submit'][step - 1]} />
    <div style={{ background: T.card, border: `1px solid ${T.line}`, borderRadius: 16, padding: '20px 18px', marginTop: 4 }}>
      {step === 1 && <Step1 a={a} up={up} qr={qr} />}
      {step === 2 && <Step2 a={a} up={up} />}
      {step === 3 && <Step3 a={a} setA={setA} />}
      {step === 4 && <Step4 a={a} qr={qr} />}
    </div>
    <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
      {step > 1 && <button onClick={() => go(step - 1, step * 20)} style={btn(false)}>Back</button>}
      {step < TOTAL && <button onClick={() => go(step + 1, (step + 1) * 20)} style={btn(true)}>Continue</button>}
      {step === TOTAL && <button onClick={submit} disabled={busy} style={btn(true)}>{busy ? 'Submitting…' : 'Submit'}</button>}
    </div>
    <p style={{ fontSize: 11, color: T.sub, textAlign: 'center', marginTop: 14 }}>🔒 Do not enter Social Security, banking, or medical details here. Your agent will collect anything else securely later.</p>
  </>);
}

function Step1({ a, up, qr }) {
  const sel = a.product_line || qr?.product_line || '';
  return <>
    <H>What coverage are you interested in?</H>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 18 }}>
      {PRODUCTS.map(([k, l]) => <Choice key={k} on={sel === k} onClick={() => up({ product_line: k })}>{l}</Choice>)}
    </div>
    <H>Are you currently covered?</H>
    <div style={{ display: 'flex', gap: 8 }}>
      {['Yes', 'No', 'Not sure'].map((x) => <Choice key={x} on={a.currently_covered === x} onClick={() => up({ currently_covered: x })}>{x}</Choice>)}
    </div>
  </>;
}
function Step2({ a, up }) {
  return <>
    <H>Your contact information</H>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
      <Field label="First name" v={a.first_name} on={(v) => up({ first_name: v })} />
      <Field label="Last name" v={a.last_name} on={(v) => up({ last_name: v })} />
      <Field label="Mobile phone" v={a.phone} on={(v) => up({ phone: v })} full type="tel" />
      <Field label="Email" v={a.email} on={(v) => up({ email: v })} full type="email" />
      <Field label="State" v={a.state} on={(v) => up({ state: v })} />
      <Field label="ZIP code" v={a.zip} on={(v) => up({ zip: v })} />
    </div>
    <div style={{ marginTop: 14 }}><H>Best way to reach you</H>
      <div style={{ display: 'flex', gap: 8 }}>{['Call', 'Text', 'Email'].map((x) => <Choice key={x} on={a.preferred_contact === x.toLowerCase()} onClick={() => up({ preferred_contact: x.toLowerCase() })}>{x}</Choice>)}</div>
    </div>
  </>;
}
function Step3({ a, setA }) {
  const grants = a.consent?.grants || [];
  const isOn = (ch) => grants.find((g) => g.channel === ch)?.granted === true;
  const toggle = (ch) => setA((s) => {
    const cur = s.consent?.grants || [];
    const others = cur.filter((g) => g.channel !== ch);
    const now = cur.find((g) => g.channel === ch);
    return { ...s, consent: { grants: [...others, { channel: ch, granted: !(now?.granted), product_line: s.product_line || 'general', purpose: 'marketing', disclosure_version: 'agency_default' }] } };
  });
  return <>
    <H>How may the agency contact you?</H>
    <p style={{ fontSize: 12.5, color: T.sub, margin: '0 0 14px', lineHeight: 1.5 }}>Choose each separately — nothing is selected for you. Declining optional messages won't stop you from requesting an appointment.</p>
    <div style={{ display: 'grid', gap: 10 }}>
      {CHANNELS.map(([ch, label, purpose]) => (
        <button key={ch} onClick={() => toggle(ch)} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', textAlign: 'left', padding: '13px 14px', borderRadius: 12, border: `1px solid ${isOn(ch) ? T.brand : T.line}`, background: isOn(ch) ? 'rgba(14,124,134,0.06)' : '#fff', cursor: 'pointer' }}>
          <span style={{ width: 22, height: 22, flexShrink: 0, borderRadius: 6, border: `2px solid ${isOn(ch) ? T.brand : '#B7C6CF'}`, background: isOn(ch) ? T.brand : '#fff', color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 900, fontSize: 13, marginTop: 1 }}>{isOn(ch) ? '✓' : ''}</span>
          <span><span style={{ fontWeight: 600, fontSize: 14.5, color: T.ink }}>{label}</span><br /><span style={{ fontSize: 12, color: T.sub, lineHeight: 1.45 }}>{purpose}</span></span>
        </button>
      ))}
    </div>
    <p style={{ fontSize: 11, color: T.sub, marginTop: 12, lineHeight: 1.5 }}>You can revoke any permission at any time by replying STOP to texts, unsubscribing from emails, or telling your agent. The agency's approved disclosures apply to each channel.</p>
  </>;
}
function Step4({ a, qr }) {
  const grants = (a.consent?.grants || []).filter((g) => g.granted);
  const row = (k, v) => v ? <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '7px 0', borderBottom: `1px solid ${T.line}`, fontSize: 14 }}><span style={{ color: T.sub }}>{k}</span><span style={{ fontWeight: 500, textAlign: 'right' }}>{v}</span></div> : null;
  return <>
    <H>Review &amp; submit</H>
    {row('Name', [a.first_name, a.last_name].filter(Boolean).join(' '))}
    {row('Coverage', PRODUCTS.find((p) => p[0] === (a.product_line || qr?.product_line))?.[1])}
    {row('Phone', a.phone)}
    {row('Email', a.email)}
    {row('Location', [a.state, a.zip].filter(Boolean).join(' '))}
    {row('Contact permissions', grants.length ? grants.map((g) => CHANNELS.find((c) => c[0] === g.channel)?.[1]).join(', ') : 'None selected')}
    <p style={{ fontSize: 12.5, color: T.sub, marginTop: 16, lineHeight: 1.55, background: '#EEF4F6', border: `1px solid ${T.line}`, borderRadius: 10, padding: '12px 14px' }}>I confirm the information above is accurate to the best of my knowledge. This general confirmation is separate from any product-specific authorization my agent may review with me later.</p>
  </>;
}

// ── primitives ──
const btn = (primary) => ({ flex: 1, padding: '15px 0', borderRadius: 12, border: primary ? 'none' : `1px solid ${T.line}`, background: primary ? T.brand : '#fff', color: primary ? '#fff' : T.ink, fontWeight: 700, fontSize: 15.5, cursor: 'pointer', fontFamily: font });
function H({ children }) { return <div style={{ fontWeight: 700, fontSize: 15.5, margin: '2px 0 10px' }}>{children}</div>; }
function Pill({ children }) { return <span style={{ display: 'inline-block', background: 'rgba(14,124,134,0.1)', color: T.brandDark, fontWeight: 600, fontSize: 12.5, padding: '6px 12px', borderRadius: 999 }}>{children}</span>; }
function Choice({ children, on, onClick }) { return <button onClick={onClick} style={{ padding: '10px 15px', borderRadius: 999, border: `1px solid ${on ? T.brand : T.line}`, background: on ? T.brand : '#fff', color: on ? '#fff' : T.ink, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: font }}>{children}</button>; }
function Field({ label, v, on, full, type = 'text' }) {
  return <label style={{ gridColumn: full ? '1 / -1' : 'auto', display: 'block' }}>
    <span style={{ fontSize: 12, color: T.sub, display: 'block', marginBottom: 4 }}>{label}</span>
    <input type={type} value={v || ''} onChange={(e) => on(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', padding: '11px 12px', borderRadius: 10, border: `1px solid ${T.line}`, fontSize: 16, fontFamily: font, color: T.ink, outline: 'none' }} onFocus={(e) => (e.target.style.borderColor = T.brand)} onBlur={(e) => (e.target.style.borderColor = T.line)} />
  </label>;
}
function Progress({ step, total, label }) {
  return <div style={{ margin: '22px 0 14px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: T.sub, marginBottom: 7 }}><span>Step {step} of {total}</span><span style={{ fontWeight: 600, color: T.ink }}>{label}</span></div>
    <div style={{ height: 6, background: T.line, borderRadius: 999, overflow: 'hidden' }}><div style={{ width: `${(step / total) * 100}%`, height: '100%', background: T.brand, transition: 'width 240ms cubic-bezier(0.22,1,0.36,1)' }} /></div>
  </div>;
}
function Center({ title, body }) { return <div style={{ paddingTop: 120, textAlign: 'center' }}><h1 style={{ fontSize: 20 }}>{title}</h1><p style={{ color: T.sub }}>{body}</p></div>; }
